export type SolarPortConfig = '1inP' | '2inP'
export type SolarPortPostType = 'singlePost' | 'twinPost'
export type SolarPortRelativeTilt = 20 | 25 | 30
export type SolarPortFoundationType = 'xAnchor' | 'pile2m' | 'pile1_5m' | 'ballastBase' | 'groundScrew' | 'directFix'

export type SolarPortComponentKey =
  | 'rafter'
  | 'rearUpright'
  | 'frontUpright'
  | 'uprightBrace'
  | 'externalWindBrace'
  | 'internalWindBrace1600'
  | 'internalWindBrace2600'
  | 'purlin3490'
  | 'purlin2490'
  | 'purlin2990'
  | 'joinerCommon'
  | 'extenderJoiner'
  | 'midClampCap'
  | 'midClampHex'
  | 'endClampCap'
  | 'endClampHex'
  | 'purlinEndCap'
  | 'serratedFlangeNut'
  | 'serratedFlangeBolt'

export interface BomLookupTable {
  minPanels: number
  maxPanels: number
  step: number
  components: Partial<Record<SolarPortComponentKey, number[]>>
}

export const COMPONENT_CODES_1INP = {
  rafter: '1261-CRAF-1390',
  rearUpright: { 20: '1261-RUP-1inP-20', 25: '1261-RUP-1inP-25', 30: '1261-RUP-1inP-30' } as Record<number, string>,
  frontUpright: '1261-FUP-1inP',
  uprightBrace: '1261-BBR-002',
  externalWindBrace: '0000-WBR-001',
  internalWindBrace1600: '0000-WBR-003',
  internalWindBrace2600: '0000-WBR-005',
  purlin3490: '1261-PUR-3490',
  purlin2490: '1261-PUR-2490',
  purlin2990: '1261-PUR-2990',
  joinerCommon: '0000-JRC-105',
  extenderJoiner: '0000-JRC-106',
  midClampCap: '0000-MCA-009',
  midClampHex: '0000-MCA-010',
  endClampCap: { 30: '0000-ECA-019', 35: '0000-ECA-027' } as Record<number, string>,
  endClampHex: { 30: '0000-ECA-020', 35: '0000-ECA-028' } as Record<number, string>,
  purlinEndCap: '0000-EDC-003',
  serratedFlangeNut: 'SPS - M12',
  serratedFlangeBolt: 'SPS - M12 x 26',
}

export const COMPONENT_CODES_2INP = {
  rafter: { standard: '1261-CRAF-3265', tall: '1261-CRAF-3818' },
  rearUpright: { 20: '1261-RUP-20', 25: '1261-RUP-25', 30: '1261-RUP-30' } as Record<number, string>,
  frontUpright: '1261-FUP-001',
  uprightBrace: '1261-BBR-001',
  externalWindBrace: '0000-WBR-001',
  internalWindBrace1600: '0000-WBR-003',
  internalWindBrace2600: '0000-WBR-005',
  purlin3490: '1261-PUR-3490',
  purlin2490: '1261-PUR-2490',
  purlin2990: '1261-PUR-2990',
  joinerCommon: '0000-JRC-105',
  extenderJoiner: '0000-JRC-106',
  midClampCap: '0000-MCA-009',
  midClampHex: '0000-MCA-010',
  endClampCap: { 30: '0000-ECA-019', 35: '0000-ECA-027' } as Record<number, string>,
  endClampHex: { 30: '0000-ECA-020', 35: '0000-ECA-028' } as Record<number, string>,
  purlinEndCap: '0000-EDC-001',
  serratedFlangeNut: 'SPS - M12',
  serratedFlangeBolt: 'SPS - M12 x 26',
}

export interface FoundationItem {
  code: string
  quantity: number
}

export const FOUNDATION_ITEMS: Record<SolarPortFoundationType, FoundationItem[]> = {
  pile2m: [
    { code: '1261-PIL-2000', quantity: 1 },
    { code: 'SPS - M12', quantity: 2 },
    { code: 'SPS - M12 x 26', quantity: 2 },
  ],
  pile1_5m: [
    { code: '1261-PIL-1500', quantity: 1 },
    { code: 'SPS - M12', quantity: 2 },
    { code: 'SPS - M12 x 26', quantity: 2 },
  ],
  xAnchor: [
    { code: '0000-XRB-101', quantity: 1 },
    { code: '0000-XRS-104', quantity: 4 },
    { code: 'SPS - M12', quantity: 2 },
    { code: 'SPS - M12 x 26', quantity: 2 },
  ],
  ballastBase: [
    { code: '0000-AJU-004', quantity: 1 },
    { code: '0000-BAP-101', quantity: 1 },
    { code: '0000-BRA-001', quantity: 2 },
    { code: 'M12 - ISO 10511', quantity: 8 },
    { code: 'M12 - ISO 7089', quantity: 16 },
    { code: 'M12 x 30 - ISO 4017', quantity: 8 },
    { code: '0000-SQW-001', quantity: 4 },
  ],
  groundScrew: [
    { code: '0000-BRA-002', quantity: 1 },
    { code: '0000-GRS-001', quantity: 1 },
    { code: 'SPS - M12 x 26', quantity: 2 },
    { code: 'SPS - M12', quantity: 2 },
    { code: 'ISO 4017 M16 x 30', quantity: 1 },
    { code: 'ISO 7089 M16', quantity: 2 },
  ],
  directFix: [
    { code: '0000-AJU-004', quantity: 1 },
    { code: '0000-BRA-001', quantity: 2 },
    { code: 'SPS - M12', quantity: 6 },
    { code: 'SPS - M12 x 26', quantity: 6 },
    { code: '0000-SQW-001', quantity: 2 },
    { code: 'M12 x 145mm - ISO 4762', quantity: 2 },
  ],
}

export const PANEL_LIMITS = {
  '1inP': { min: 3, max: 30 },
  '2inP': { min: 8, max: 60 },
} as const

export const MIN_GROUND_CLEARANCE_MM: Record<SolarPortConfig, Record<SolarPortRelativeTilt, number>> = {
  '2inP': { 20: 785, 25: 735, 30: 680 },
  '1inP': { 20: 744, 25: 759, 30: 773 },
}

export const RELATIVE_TILT_OPTIONS: SolarPortRelativeTilt[] = [20, 25, 30]
export const PANEL_HEIGHT_THRESHOLD_MM = 2000
export const DEFAULT_END_CLAMP_DEPTH = 30
